/**
 * CFC Quick Add — Fullscreen overlay quick-add for Featured Collection
 * Custom element: <cfc-quick-add>
 *
 * Handles:
 * - Toggle fullscreen overlay (backdrop + panel)
 * - Size option selection
 * - Add to cart via Shopify AJAX API
 * - Cart drawer integration (Dawn native)
 *
 * NOTE: The panel and backdrop are moved to <body> on init
 * to escape any parent transform/overflow that would break position:fixed.
 */

if (!customElements.get('cfc-quick-add')) {
  customElements.define(
    'cfc-quick-add',
    class CfcQuickAdd extends HTMLElement {
      constructor() {
        super();
        this.triggerBtn = this.querySelector('.cfc__quick-btn');
        this.selectedVariantId = null;
        this.isSingleAdd = this.triggerBtn && this.triggerBtn.hasAttribute('data-single-add');

        // Panel & backdrop will be moved to body
        this.panel = null;
        this.backdrop = null;
        this.addBtn = null;
        this.errorEl = null;
      }

      connectedCallback() {
        // ── Single-variant mode: "+" button adds directly to cart ──
        if (this.isSingleAdd) {
          this.selectedVariantId = this.triggerBtn.dataset.variantId;
          this.triggerBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.addToCart(this.triggerBtn);
          });
          return;
        }

        // ── Multi-variant panel mode ──
        const panelEl = this.querySelector('.cfc__quick-panel');
        const backdropEl = this.querySelector('.cfc__quick-backdrop');
        if (!panelEl) return;

        // Move panel and backdrop to <body> to escape parent transforms
        document.body.appendChild(backdropEl);
        document.body.appendChild(panelEl);

        // Store references after move
        this.panel = panelEl;
        this.backdrop = backdropEl;
        this.addBtn = this.panel.querySelector('.cfc__add-to-cart');
        this.errorEl = this.panel.querySelector('.cfc__add-error');

        // Trigger button (the "+" icon)
        if (this.triggerBtn) {
          this.triggerBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.open();
          });
        }

        // Close buttons
        this.panel.querySelectorAll('.cfc__panel-close, .cfc__panel-close--desktop').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.close();
          });
        });

        // Backdrop close
        if (this.backdrop) {
          this.backdrop.addEventListener('click', () => this.close());
        }

        // Size option selection (event delegation)
        const sizeGrid = this.panel.querySelector('.cfc__size-grid');
        if (sizeGrid) {
          sizeGrid.addEventListener('click', (e) => {
            const option = e.target.closest('.cfc__size-option');
            if (!option || option.classList.contains('is-unavailable')) return;

            // Deselect all, select clicked
            sizeGrid.querySelectorAll('.cfc__size-option').forEach((o) => o.classList.remove('is-selected'));
            option.classList.add('is-selected');
            this.selectedVariantId = option.dataset.variantId;

            // Enable add to cart
            if (this.addBtn) this.addBtn.disabled = false;
            this.clearError();
          });
        }

        // Pre-select first available size
        if (sizeGrid) {
          const firstAvailable = sizeGrid.querySelector('.cfc__size-option:not(.is-unavailable)');
          if (firstAvailable) {
            firstAvailable.classList.add('is-selected');
            this.selectedVariantId = firstAvailable.dataset.variantId;
          }
        }

        // Add to cart button
        if (this.addBtn) {
          this.addBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.addToCart();
          });
        }

        // Close on Escape key
        this._onKeyUp = (e) => {
          if (e.code === 'Escape' && this.isOpen()) this.close();
        };
        document.addEventListener('keyup', this._onKeyUp);
      }

      disconnectedCallback() {
        if (this._onKeyUp) document.removeEventListener('keyup', this._onKeyUp);
        // Clean up moved elements
        if (this.panel && this.panel.parentNode === document.body) {
          document.body.removeChild(this.panel);
        }
        if (this.backdrop && this.backdrop.parentNode === document.body) {
          document.body.removeChild(this.backdrop);
        }
      }

      isOpen() {
        return this.panel && this.panel.classList.contains('is-open');
      }

      open() {
        // First open: load THIS card's deferred panel thumbnail (no request while panel is closed)
        if (this.panel) {
          this.panel.querySelectorAll('img[data-src]').forEach((img) => {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          });
        }

        // Close any other open panels first
        document.querySelectorAll('.cfc__quick-panel.is-open').forEach((p) => {
          p.classList.remove('is-open');
        });
        document.querySelectorAll('.cfc__quick-backdrop.is-open').forEach((b) => {
          b.classList.remove('is-open');
        });

        this.panel.classList.add('is-open');
        if (this.backdrop) this.backdrop.classList.add('is-open');

        // Prevent body scroll while overlay is open
        document.body.classList.add('overflow-hidden');
      }

      close() {
        this.panel.classList.remove('is-open');
        if (this.backdrop) this.backdrop.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
        this.clearError();
      }

      clearError() {
        if (this.errorEl) this.errorEl.textContent = '';
      }

      async addToCart(loadingEl) {
        if (!this.selectedVariantId) {
          if (this.errorEl) this.errorEl.textContent = 'Please select a size';
          return;
        }

        const btn = loadingEl || this.addBtn;
        if (btn && btn.classList.contains('is-loading')) return;

        if (btn) btn.classList.add('is-loading');
        this.clearError();

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              id: parseInt(this.selectedVariantId),
              quantity: 1,
              sections: this.getCartSections(),
              sections_url: window.location.pathname,
            }),
          });

          const data = await response.json();

          if (data.status) {
            // Error from Shopify (e.g. sold out)
            if (this.errorEl) this.errorEl.textContent = data.description || data.message || 'Error adding to cart';
            return;
          }

          // Success — close panel and open cart drawer
          if (this.panel) this.close();

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
          } else {
            // Fallback: cart notification or redirect
            const cartNotification = document.querySelector('cart-notification');
            if (cartNotification && typeof cartNotification.renderContents === 'function') {
              cartNotification.renderContents(data);
            } else {
              window.location.href = window.routes?.cart_url || '/cart';
            }
          }

          // Publish cart update event if available (Dawn pub/sub)
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cfc-quick-add',
              productVariantId: this.selectedVariantId,
              cartData: data,
            });
          }
        } catch (err) {
          console.error('CFC Quick Add error:', err);
          if (this.errorEl) this.errorEl.textContent = 'Something went wrong. Please try again.';
        } finally {
          if (btn) btn.classList.remove('is-loading');
        }
      }

      getCartSections() {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          return cartDrawer.getSectionsToRender().map((s) => s.id);
        }
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
          return cartNotification.getSectionsToRender().map((s) => s.id);
        }
        return ['cart-drawer', 'cart-icon-bubble'];
      }
    }
  );
}
